import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { fetchChatSessions, fetchChatSession, deleteChatHistory } from '../api/chatHistory';
import './ChatHistoryPage.css';

const ChatHistoryPage = () => {
  const { t } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadChatSessions();
  }, []);

  const loadChatSessions = async () => {
    try {
      setLoading(true);
      const response = await fetchChatSessions();
      if (response.success) {
        setSessions(response.data);
      } else {
        setError('Failed to load chat sessions');
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
      setError('Error loading chat sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = async (sessionId) => {
    try {
      const response = await fetchChatSession(sessionId);
      if (response.success) {
        setSelectedSession(response.data);
      } else {
        setError('Failed to load chat session');
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
      setError('Error loading chat session');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this chat session?')) {
      try {
        const response = await deleteChatHistory(sessionId);
        if (response.success) {
          setSessions(sessions.filter(session => session.sessionId !== sessionId));
          if (selectedSession && selectedSession.sessionId === sessionId) {
            setSelectedSession(null);
          }
        } else {
          setError('Failed to delete chat session');
        }
      } catch (error) {
        console.error('Error deleting chat session:', error);
        setError('Error deleting chat session');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getSessionTitle = (session) => {
    if (session.tripDetails && session.tripDetails.destination) {
      return `Trip to ${session.tripDetails.destination}`;
    }
    return `Chat Session ${session.sessionId.slice(-8)}`;
  };

  if (loading) {
    return (
      <div className="chat-history-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading chat history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-history-page">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadChatSessions} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-history-page">
      <div className="history-container">
        <div className="history-header">
          <h2>Chat History</h2>
          <p>View and manage your previous conversations with Mellow AI</p>
        </div>

        <div className="history-content">
          <div className="sessions-list">
            <h3>Your Chat Sessions</h3>
            {sessions.length === 0 ? (
              <div className="no-sessions">
                <p>No chat sessions found.</p>
                <p>Start chatting with Mellow AI to see your history here!</p>
              </div>
            ) : (
              <div className="sessions-grid">
                {sessions.map((session) => (
                  <div 
                    key={session.sessionId} 
                    className={`session-card ${selectedSession?.sessionId === session.sessionId ? 'selected' : ''}`}
                    onClick={() => handleSessionClick(session.sessionId)}
                  >
                    <div className="session-header">
                      <h4>{getSessionTitle(session)}</h4>
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.sessionId);
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="session-details">
                      <p className="session-date">
                        {formatDate(session.updatedAt)}
                      </p>
                      {session.tripDetails && session.tripDetails.destination && (
                        <p className="session-destination">
                          📍 {session.tripDetails.destination}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="session-detail">
            {selectedSession ? (
              <div className="selected-session">
                <div className="session-info">
                  <h3>{getSessionTitle(selectedSession)}</h3>
                  <p className="session-date">
                    Created: {formatDate(selectedSession.createdAt)}
                  </p>
                  <p className="session-date">
                    Last updated: {formatDate(selectedSession.updatedAt)}
                  </p>
                </div>

                <div className="messages-list">
                  {selectedSession.messages.map((message) => (
                    <div key={message.id} className={`history-message ${message.type}`}>
                      <div className="message-content">
                        {message.type === 'ai' ? (
                          <div dangerouslySetInnerHTML={{ 
                            __html: message.content.replace(/\n/g, '<br>') 
                          }} />
                        ) : (
                          message.content
                        )}
                      </div>
                      <div className="message-time">
                        {formatDate(message.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-selection">
                <h3>Select a Session</h3>
                <p>Choose a chat session from the list to view the conversation.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryPage; 